import type { Timeout } from '../../types'
import { loadUserIfNeeded } from '../actions/loadUserIfNeeded'
import { getUsersState } from '../app-state/state.users'
// import { getUsersState } from '../app-state/state.users'
import { previewSnapshotUrl } from '../mfcLogic/avatarPreviewUrl'
import { getTimeoutFromAnimationUeopt } from '../mfcLogic/settings'
import { randomize } from '../utilities/math'
import { pubSubTimer } from './pubSub'
import { isCallbacksEmpty } from './pubSub/createPubSub'
import { SETTINGSSTATE } from './state.settings'
import { getStateUser } from './state.users'

export enum SnapSize {
    small = '160x120',
    medium = '320x240',
    large = '640x480',
}

const SNAPSIZEMAP = {
    '160x120': 0,
    '320x240': 1,
    '640x480': 2,
}

export enum TimerEvent {
    STATE = 'state',
}

export type ThrottleTimers = {
    mainTimer: Timeout
    mainTimerPaused: boolean
    messages: {
        list: Set<string>
        timer: Timeout
    }
    snaps: {
        [id: string]: {
            srcUrl: string
            timestamp: number
            throttle: number
            size: SnapSize
            subbed: boolean
            staticImage: boolean
            staticImageOverride: boolean
        }
    }
    news: Timeout
}

export const THROTTLE_TIMERS: ThrottleTimers = {
    mainTimer: null,
    mainTimerPaused: false,
    messages: {
        list: new Set<string>(),
        timer: null,
    },
    snaps: {},
    news: null,
}

if (typeof window !== 'undefined') {
    const SNAPS_PER_CYCLE = 40
    const processSnaps = async () => {
        // TODO: this should be able to better limit the updates to
        // x amount in some defined time span
        const now = Date.now()
        // eslint-disable-next-line
        let counter = 0
        const snapKeys = Object.keys(THROTTLE_TIMERS.snaps)
        for (const snapKey of snapKeys) {
            const posterTimer = THROTTLE_TIMERS.snaps[snapKey]

            // if we have some that aren't processed yet then just do them
            if (counter >= SNAPS_PER_CYCLE && posterTimer?.timestamp !== 0)
                return

            if (now - posterTimer.timestamp > posterTimer.throttle) {
                counter++
                // console.log('[snap loop]', snapKey, idToSlug[snapKey], {
                //     ...posterTimer,
                // })
                if (posterTimer.staticImage) {
                    // while we're static just keep kicking the can down the road
                    posterTimer.timestamp = now
                    return
                }
                const { channel, isNGVideo, phase, server } =
                    await loadUserIfNeeded(Number(snapKey))

                posterTimer.timestamp = now

                // recalc the throttle to ensure we update when a user changes their setting
                posterTimer.throttle = Math.max(
                    getTimeoutFromAnimationUeopt(
                        SETTINGSSTATE.DEFAULT.animationSpeed,
                    ) + randomize(-500, 500),
                    1000,
                )
                // create a new srcUrl
                posterTimer.srcUrl = phase
                    ? previewSnapshotUrl(
                          (isNGVideo ? phase + '_' : '') + channel,
                          server,
                          now,
                          posterTimer.size === SnapSize.large,
                          posterTimer.size === SnapSize.small,
                      )
                    : ''
                const eventKey = `${TimerEvent.STATE}::${snapKey}`
                if (!isCallbacksEmpty('TIMER_', eventKey)) {
                    const newPosterTimer = { ...posterTimer }
                    THROTTLE_TIMERS.snaps[snapKey] = newPosterTimer
                    pubSubTimer.emit(eventKey, newPosterTimer, true)
                } else {
                    // start toggle the subbed flag if there's no pubsub subs
                    // and clean them up next cycle
                    if (!posterTimer.subbed)
                        delete THROTTLE_TIMERS.snaps[snapKey]
                    else posterTimer.subbed = false
                }
            }
        }
    }
    // this should check and update the src urls for any
    // active snaps we have subscribed based on their throttles
    THROTTLE_TIMERS.mainTimer = setInterval(() => {
        const usersState = getUsersState()
        if (
            THROTTLE_TIMERS.mainTimerPaused ||
            !usersState ||
            !usersState.modelsLoaded
        )
            return
        void processSnaps()
    }, 500)
    window.__MFC_APP_THROTTLE_TIMERS__ = THROTTLE_TIMERS
}

const initialDelay = (throttle: number, min = 0) => randomize(min, throttle)

export const registerSnapTimer = (
    id: number,
    size: SnapSize,
    addThrottleWobble = true,
    calcNow = true,
) => {
    // temporary adding slug for debug ease
    const snapKey = `${id}`
    const posterTimer = THROTTLE_TIMERS.snaps[snapKey]
    const currentOverride = posterTimer?.staticImageOverride || false
    const throttle = getTimeoutFromAnimationUeopt(
        SETTINGSSTATE.DEFAULT.animationSpeed,
    )
    const foundThrottle = throttle ? throttle : (posterTimer?.throttle ?? 1000)
    const foundTimestamp = addThrottleWobble
        ? initialDelay(foundThrottle, foundThrottle / 2)
        : posterTimer?.timestamp || 0
    const foundSize =
        posterTimer?.size && SNAPSIZEMAP[posterTimer.size] > SNAPSIZEMAP[size]
            ? posterTimer.size
            : size
    // console.log(
    //     '[registerSnapTimer]',
    //     id,
    //     {
    //         size,
    //     },
    //     posterTimer,
    // )

    if (calcNow) {
        const user = getStateUser(id)
        if (!user) return
        const { channel, isNGVideo, phase, server } = user
        const now = Date.now()

        const newPosterTimer = {
            size: foundSize,
            throttle: addThrottleWobble
                ? Math.max(foundThrottle + randomize(-500, 500), 1000)
                : foundThrottle,
            subbed: true,
            staticImage: false,
            staticImageOverride: currentOverride,
            timestamp: foundTimestamp,
            srcUrl: phase
                ? previewSnapshotUrl(
                      (isNGVideo ? phase + '_' : '') + channel,
                      server,
                      now,
                      size === SnapSize.large,
                      size === SnapSize.small,
                  )
                : '',
        }
        THROTTLE_TIMERS.snaps[snapKey] = newPosterTimer
    } else {
        const newPosterTimer = {
            size: foundSize,
            throttle: addThrottleWobble
                ? Math.max(foundThrottle + randomize(-500, 500), 1000)
                : foundThrottle,
            subbed: true,
            staticImage: false,
            staticImageOverride: currentOverride,
            timestamp: 0,
            srcUrl: '',
        }
        THROTTLE_TIMERS.snaps[snapKey] = newPosterTimer
    }
    return THROTTLE_TIMERS.snaps[snapKey].srcUrl
}

export const forceSnapUpdate = (id: number) => {
    const snapKey = `${id}`
    const posterTimer = THROTTLE_TIMERS.snaps[snapKey]
    if (
        posterTimer &&
        posterTimer.subbed &&
        posterTimer.staticImage &&
        posterTimer.srcUrl
    ) {
        // if we're forcing because we've changed from being static
        // and we already have a src url then we shouldn't calc now,
        // we should delay a full cycle so we aren't immediately
        // updating all the snaps
        posterTimer.subbed = true
        posterTimer.staticImage = false
        posterTimer.staticImageOverride = false
        posterTimer.timestamp = Date.now()
        /// we should be careful about blasting away the reference in certain cases... this is probably one of them?
        // THROTTLE_TIMERS.snaps[snapKey] = newPosterTimer
    } else if (!posterTimer || !posterTimer.subbed) {
        // console.log('[forceSnapUpdate] registering', id, idToSlug[id])
        registerSnapTimer(id, posterTimer?.size || SnapSize.large)
    }
}
export const setStaticImage = (id: number, staticValue: boolean) => {
    const snapKey = `${id}`
    const posterTimer = THROTTLE_TIMERS.snaps[snapKey]
    // console.trace('[setStaticImage]', id, idToSlug[id], posterTimer)
    if (posterTimer && !posterTimer.staticImageOverride) {
        posterTimer.staticImage = staticValue
        THROTTLE_TIMERS.snaps[snapKey] = { ...posterTimer }
    }
}
export const setStaticImageOverride = (id: number) => {
    const snapKey = `${id}`
    const posterTimer = THROTTLE_TIMERS.snaps[snapKey]
    // console.log('[setStaticImageOverride]', id, idToSlug[id], posterTimer)
    if (posterTimer) {
        posterTimer.staticImageOverride = true
        posterTimer.staticImage = false
        THROTTLE_TIMERS.snaps[snapKey] = { ...posterTimer }
    }
}
